/**
 * MegaMenu Module
 * PC版: マウスオーバーで表示
 * SP版: アコーディオン（クリックで開閉）
 */
export class MegaMenu {
  constructor() {
    this.megamenus = document.querySelectorAll('.js-megamenu');
    this.breakpoint = 960;
    this.eventHandlers = new Map();
    this.resizeTimer = null;
    this.resizeHandler = null;
    this.wasPC = this.isPC();
    
    if (this.megamenus.length > 0) {
      this.init();
    }
  }
  
  isPC() {
    return window.innerWidth >= this.breakpoint;
  }
  
  init() {
    this.removeEventListeners();
    this.setupResizeHandler();
    this.setupEventListeners();
  }
  
  /**
   * リサイズハンドラーを設定（デバウンス付き）
   */
  setupResizeHandler() {
    // 既存のハンドラーを削除
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    this.resizeHandler = () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      
      this.resizeTimer = setTimeout(() => {
        const isPCNow = this.isPC();
        
        if (!this.wasPC && isPCNow) {
          this.closeAll();
        }
        
        if (this.wasPC !== isPCNow) {
          this.wasPC = isPCNow;
          this.setupEventListeners();
        }
      }, 150);
    };
    
    window.addEventListener('resize', this.resizeHandler);
  }
  
  /**
   * イベントリスナーを設定
   */
  setupEventListeners() {
    if (this.megamenus.length === 0) {
      return;
    }
    
    // 各メガメニューに対してイベントを設定
    this.megamenus.forEach((megamenu) => {
      const trigger = megamenu.querySelector('.js-megamenu__trigger');
      if (!trigger) {
        return;
      }
      
      // 既存のハンドラーを削除
      this.removeMenuEventListeners(megamenu);
      
      if (this.isPC()) {
        this.initPC(megamenu, trigger);
      } else {
        this.initSP(megamenu, trigger);
      }
    });
  }
  
  /**
   * 指定されたメガメニューのイベントリスナーを削除
   */
  removeMenuEventListeners(megamenu) {
    const handlers = this.eventHandlers.get(megamenu);
    if (handlers) {
      handlers.forEach(({ event, handler, element }) => {
        const target = element || megamenu;
        target.removeEventListener(event, handler);
      });
      this.eventHandlers.delete(megamenu);
    }
  }
  
  /**
   * PC版の初期化（マウスオーバー）
   */
  initPC(megamenu, trigger) {
    const handlers = [];
    const content = megamenu.querySelector('.js-megamenu__content');
    let closeTimer = null;
    
    // メガメニューの範囲を計算
    const getBounds = () => {
      const mRect = megamenu.getBoundingClientRect();
      if (!content) {
        return { top: mRect.top, bottom: mRect.bottom, left: mRect.left, right: mRect.right };
      }
      const cRect = content.getBoundingClientRect();
      return {
        top: mRect.top,
        bottom: Math.max(mRect.bottom, cRect.bottom),
        left: Math.min(mRect.left, cRect.left),
        right: Math.max(mRect.right, cRect.right)
      };
    };
    
    // マウスが範囲内にあるかチェック
    const isInBounds = (x, y, bounds) => {
      return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
    };
    
    const openMenu = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      this.closeOthers(megamenu);
      megamenu.classList.add('is-megamenu__open');
    };
    
    const closeMenu = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
      }
      closeTimer = setTimeout(() => {
        megamenu.classList.remove('is-megamenu__open');
        closeTimer = null;
      }, 150);
    };
    
    // マウスがメガメニュー要素に入った時
    const mouseenterHandler = openMenu;
    megamenu.addEventListener('mouseenter', mouseenterHandler);
    handlers.push({ event: 'mouseenter', handler: mouseenterHandler });
    
    // マウスがメガメニュー要素から出た時
    const mouseleaveHandler = (e) => {
      const bounds = getBounds();
      if (!isInBounds(e.clientX, e.clientY, bounds)) {
        closeMenu();
      }
    };
    megamenu.addEventListener('mouseleave', mouseleaveHandler);
    handlers.push({ event: 'mouseleave', handler: mouseleaveHandler });
    
    // コンテンツエリアのイベント
    if (content) {
      const contentEnter = () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      };
      const contentLeave = closeMenu;
      
      content.addEventListener('mouseenter', contentEnter);
      content.addEventListener('mouseleave', contentLeave);
      handlers.push(
        { event: 'mouseenter', handler: contentEnter, element: content },
        { event: 'mouseleave', handler: contentLeave, element: content }
      );
    }
    
    this.eventHandlers.set(megamenu, handlers);
  }
  
  /**
   * SP版の初期化（アコーディオン）
   */
  initSP(megamenu, trigger) {
    const handlers = [];
    const clickHandler = (e) => {
      e.preventDefault();
      this.closeOthers(megamenu);
      megamenu.classList.toggle('is-megamenu__open');
    };
    
    trigger.addEventListener('click', clickHandler);
    handlers.push({ event: 'click', handler: clickHandler, element: trigger });
    this.eventHandlers.set(megamenu, handlers);
  }
  
  /**
   * 指定されたメガメニュー以外を閉じる
   */
  closeOthers(currentMegamenu) {
    const isSP = !this.isPC();
    
    this.megamenus.forEach((megamenu) => {
      if (megamenu !== currentMegamenu && megamenu.classList.contains('is-megamenu__open')) {
        if (isSP) {
          // SP版の場合、アニメーションなしで即座に閉じる
          const content = megamenu.querySelector('.js-megamenu__content');
          if (content) {
            // transitionを一時的に無効化
            const originalTransition = content.style.transition || '';
            content.style.transition = 'none';
            
            // クラスを削除
            megamenu.classList.remove('is-megamenu__open');
            
            // 次のフレームでtransitionを元に戻す
            requestAnimationFrame(() => {
              content.style.transition = originalTransition;
            });
          } else {
            megamenu.classList.remove('is-megamenu__open');
          }
        } else {
          // PC版の場合は通常通り閉じる
          megamenu.classList.remove('is-megamenu__open');
        }
      }
    });
  }
  
  /**
   * 全てのメガメニューを閉じる
   */
  closeAll() {
    this.megamenus.forEach((megamenu) => {
      megamenu.classList.remove('is-megamenu__open');
    });
  }
  
  /**
   * 全てのイベントリスナーを削除
   */
  removeEventListeners() {
    this.megamenus.forEach((megamenu) => {
      this.removeMenuEventListeners(megamenu);
    });
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
  }
}
